import { Link } from 'react-router-dom';
import {
  Shield,
  Bell,
  Database,
  Globe,
  Key,
  Sliders,
  Gauge,
  Briefcase,
  Layers,
  Workflow,
  Settings,
  ArrowRight,
} from 'lucide-react';
import { Heading, BodyText, Card, PageHeader } from '@aaimplatform/ui';

export interface ConfigTile {
  id: string;
  /** The surface's one name: the same string the sidebar nav item carries. */
  title: string;
  /** What the page contains today, not what it may contain later. */
  description: string;
  href: string;
  icon: React.ElementType;
  /** 'View' for a read-only surface, 'Configure' where the page saves. */
  cta: 'Configure' | 'View';
}

export const CONFIG_TILES: ConfigTile[] = [
  {
    id: 'general',
    title: 'General Settings',
    description: 'Platform name, support contact, locale, security policy, market data and notification preferences',
    href: '/platform-config/general',
    icon: Settings,
    cta: 'Configure',
  },
  {
    id: 'security',
    title: 'Security',
    description: 'Authentication, MFA, session policy, and IP allowlists',
    href: '/platform-config/security',
    icon: Shield,
    cta: 'Configure',
  },
  {
    id: 'notifications',
    title: 'Notification Defaults',
    description: 'Delivery channels, per-asset-class alert thresholds, and the daily digest (none stored yet)',
    href: '/platform-config/notifications',
    icon: Bell,
    cta: 'View',
  },
  {
    id: 'api',
    title: 'API',
    description: 'Rate limits, webhook endpoints, and API key management',
    href: '/platform-config/api',
    icon: Key,
    cta: 'Configure',
  },
  {
    id: 'data',
    title: 'Data and Storage',
    description: 'Retention policies, backup cadence, and archival rules',
    href: '/platform-config/data',
    icon: Database,
    cta: 'Configure',
  },
  {
    id: 'integrations',
    title: 'Integration Credentials',
    description: 'Third-party provider connection status and connection tests',
    href: '/platform-config/integrations',
    icon: Globe,
    cta: 'Configure',
  },
  {
    id: 'defaults',
    title: 'System Defaults',
    description: 'LTV, loan term, approval workflow and risk defaults (none stored yet)',
    href: '/platform-config/defaults',
    icon: Sliders,
    cta: 'View',
  },
  {
    id: 'rate-limits',
    title: 'Provider Rate Limits',
    description: 'Per-provider request limits by time window',
    href: '/platform-config/rate-limits',
    icon: Gauge,
    cta: 'Configure',
  },
  {
    id: 'lending',
    title: 'Lending Defaults',
    description: 'The session tenant\'s interest rates, LTV thresholds, loan limits, credit decision automation, and asset haircuts',
    href: '/platform-config/lending',
    icon: Briefcase,
    cta: 'Configure',
  },
  {
    id: 'collateral-baselines',
    title: 'Collateral Baselines',
    description: 'Aaim-seeded asset class, concentration and monitoring baselines by charter type and framework',
    href: '/platform-config/collateral-baselines',
    icon: Layers,
    cta: 'View',
  },
  {
    // Node 10 item (c), 2026-08-13. The waterfall a pledge runs through --
    // gate order, the actor per gate, and when the BINDING control instrument
    // dispatches. Authoring + allowlisting are Aaim-operator acts; the FI
    // admin's SELECTION from the allowlisted subset lives on admin-portal at
    // /admin/config/workflow-ordering (apps/admin-portal WorkflowOrdering.tsx).
    //
    // That last clause was an ASSERTION with no referent from 2026-08-13 until
    // 2026-08-17: the binding PUT had zero callers tree-wide and admin-portal
    // contained no workflow-recipe code at all, so a reader had no way to tell
    // the sentence was describing an intent rather than a surface. It names the
    // route now, which is checkable. Ruling D3, 2026-08-17.
    id: 'workflow-recipes',
    title: 'Workflow Recipes',
    description: 'Gate ordering for contract assembly, signature capture, and control-instrument dispatch',
    href: '/platform-config/workflow-recipes',
    icon: Workflow,
    cta: 'Configure',
  },
  // Sprint C15-C WP F-1 (2026-05-03): Feature Flags tile removed. Tenant
  // entitlements (PAL, LOS Preview, PE Valuation, Liquidity Network) are
  // managed per-tenant on the platform-admin SPA Tenants page. Aaim-platform
  // system flags are managed on the platform-admin /feature-flags page. The
  // admin-portal page file is preserved (and still routable for direct deep
  // links) but unwired from the navigation tile grid.
];

/**
 * Platform Configuration index. Navigation-only landing page that links to
 * the configuration sub-pages. Each tile's title is the surface's one name
 * (the sidebar nav label), its description lists what the page holds today,
 * and its call to action reads 'View' where the page cannot save.
 */
export function PlatformConfigPage() {
  return (
    <div>
      <PageHeader description="Choose a configuration area. Pages marked View are read-only." />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CONFIG_TILES.map((tile) => {
          const Icon = tile.icon;
          return (
            <Link
              key={tile.id}
              to={tile.href}
              className="group block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
            >
              <Card className="h-full">
                <div className="h-full flex flex-col">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-sm bg-elevated flex items-center justify-center">
                      <Icon className="w-5 h-5 text-secondary" aria-hidden="true" />
                    </div>
                  </div>
                  <Heading level={2} as={3} size="compact" className="mb-2 font-sans">
                    {tile.title}
                  </Heading>
                  <BodyText variant="ui" size="sm" color="secondary" className="mb-6 flex-1">
                    {tile.description}
                  </BodyText>
                  <div className="flex items-center gap-1 text-sm font-sans font-medium text-accent group-hover:gap-2 transition-all">
                    {tile.cta}
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
